import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "./Membership.css";
import Footer from "../components/Footer";

const Membership = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly"); // or "yearly"
  const [showMonthlyWarning, setShowMonthlyWarning] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);
  const [pendingPriceId, setPendingPriceId] = useState(null);
  const location = useLocation();

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const tiers = [
    {
      name: "Free Tier User",
      role: "FREE_USER",
      price: "Free",
      description: "Jump in and start solving with zero commitment.",
      features: [
        "Access to 2 basic coding problems",
        "1 solution check per day",
        "No progress saving",
        "Hints not available",
        "Importing problems disabled",
      ],
      prices: {}, // no upgrade
    },
    {
      name: "Basic Tier",
      role: "PAID_USER",
      description:
        "Level up slowly and consistently with core features unlocked.",
      features: [
        "All problems unlocked",
        "Save progress & track XP",
        // "Daily XP limit: 50 XP",
        // "Basic gamification",
        "1 imported problem per month",
        "Limited hint access",
        "Discord access",
      ],
      prices: {
        monthly: {
          label: "$9.99 / month",
          id: process.env.REACT_APP_STRIPE_BASIC_PRICE_ID,
        },
        yearly: {
          label: "$99.99 / year",
          id: process.env.REACT_APP_STRIPE_BASIC_YEARLY_PRICE_ID, // ✅ Add this env var
        },
      },
    },
    {
      name: "Premium Tier",
      role: "PREMIUM_USER",
      recommended: true,
      description: "Unlock everything and challenge yourself.",
      features: [
        "All Basic features plus:",
        "Unlimited XP & hints",
        "Access to all Learning Paths",
        "10 imported problems per month",
        "Coaching coming soon",
      ],
      prices: {
        monthly: {
          label: "$19.99 / month",
          id: process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID,
        },
        yearly: {
          label: "$199.99 / year",
          id: process.env.REACT_APP_STRIPE_PREMIUM_YEARLY_PRICE_ID,
        },
      },
    },
  ];

  const roleRank = {
    FREE_USER: 0,
    PAID_USER: 1,
    PREMIUM_USER: 2,
  };

  const isDowngradeOption = (userRole, tierRole) => {
    return roleRank[tierRole] < roleRank[userRole];
  };

  const fetchUser = async () => {
    const token = localStorage.getItem("token");
    const userIdFromStorage = localStorage.getItem("userId");
    if (!token || !userIdFromStorage) return;

    try {
      const res = await axios.get(
        `${baseUrl}/users/account/${userIdFromStorage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUser(res.data);
    } catch (err) {
      console.error("Error fetching user info:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [baseUrl]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("refresh") === "true") {
      // Refetch the user data
      fetchUser();
    }
  }, [location.search]);

  const getStripePriceId = (role) => {
    if (role === "PAID_USER")
      return process.env.REACT_APP_STRIPE_BASIC_PRICE_ID;
    if (role === "PREMIUM_USER")
      return process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID;
    return null;
  };

  const handleOpenPortal = async () => {
    if (!user?.stripeCustomerId) {
      alert("Stripe billing info not available yet. Please try again shortly.");
      return;
    }

    try {
      const res = await axios.post(
        `${baseUrl}/payments/create-customer-portal-session`,
        { customerId: user.stripeCustomerId },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      window.location.href = res.data.url;
    } catch (err) {
      console.error("Error opening Stripe portal:", err);
      alert("Could not open Stripe billing page.");
    }
  };

  const handleUpgradeClick = async (priceId) => {
    if (!user?.id) {
      alert("User ID not loaded. Please try again in a moment.");
      return;
    }

    const endpoint =
      user?.specialCode === "MAGGIE10"
        ? `${baseUrl}/payments/create-special-checkout-session`
        : `${baseUrl}/payments/create-checkout-session`;

    try {
      const res = await axios.post(
        endpoint,
        { priceId, userId: user.id },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Stripe checkout failed:", err);
      alert("Something went wrong starting checkout.");
    }
  };

  // ⏰ Helper function
  const getDaysRemaining = (dateStr) => {
    const today = new Date();
    const end = new Date(dateStr);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // 🧠 Compute days left (in your component body)
  const daysRemaining =
    user?.subscriptionEndDate && new Date(user.subscriptionEndDate) > new Date()
      ? getDaysRemaining(user.subscriptionEndDate)
      : null;

  if (loading || !user) {
    return (
      <div className="membership-container">Loading membership info...</div>
    );
  }

  return (
    <div className="membership-container">
      <h1 className="membership-title">Choose Your Membership Plan</h1>
      {daysRemaining && user.role !== "FREE_USER" && (
        <div className="subscription-warning">
          ⚠️ Your current plan will expire in <strong>{daysRemaining}</strong>{" "}
          day{daysRemaining !== 1 && "s"}.
        </div>
      )}
      <div className="billing-pill-toggle-container">
        <div className="billing-pill-toggle">
          <button
            className={`pill-option ${billingCycle === "monthly" ? "selected" : ""}`}
            onClick={() => setBillingCycle("monthly")}
          >
            Monthly
          </button>
          <button
            className={`pill-option ${billingCycle === "yearly" ? "selected" : ""}`}
            onClick={() => setBillingCycle("yearly")}
          >
            Annually
          </button>
        </div>
        <div className="save-callout">
          <span className="arrow">↖</span> Save Up To 17%
        </div>
      </div>

      <div className="membership-grid">
        {tiers.map((tier, index) => {
          const isCurrentPlan =
            tier.role === user?.role &&
            (!user?.isTrialPremium || tier.role !== "PREMIUM_USER");

          return (
            <div
              key={index}
              className={`membership-card ${
                isCurrentPlan ? "current-plan" : ""
              } ${tier.recommended ? "recommended" : ""}`}
            >
              {isCurrentPlan && (
                <div className="current-plan-label">Your Plan</div>
              )}
              {tier.recommended && (
                <div className="recommended-badge">Recommended</div>
              )}

              {/* <div className="membership-table-container">
        <table className="membership-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Free</th>
              <th>Basic</th>
              <th>Premium</th>
            </tr>
          </thead>
          <tbody>
            {featureComparison.map((item, index) => (
              <tr key={index}>
                <td>{item.feature}</td>
                <td className={`text-center ${item.free ? "check" : "cross"}`}>
                  {item.free === true ? "✔️" : item.free === false ? "❌" : item.free}
                </td>
                <td className={`text-center ${item.basic ? "check" : "cross"}`}>
                  {item.basic === true ? "✔️" : item.basic === false ? "❌" : item.basic}
                </td>
                <td className={`text-center ${item.premium ? "check" : "cross"}`}>
                  {item.premium === true ? "✔️" : item.premium === false ? "❌" : item.premium}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> */}

              <h2 className="tier-name">{tier.name}</h2>
              {/* <div className="tier-price-tag">{tier.price}</div> */}
              <div className="tier-price-tag">
                {tier.prices[billingCycle]?.label || tier.price}
              </div>

              <p className="tier-description">{tier.description}</p>

              <ul className="membership-features">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="feature-item">
                    {feature}
                  </li>
                ))}
              </ul>

              {isCurrentPlan ? (
                <button className="membership-upgrade-button selected" disabled>
                  Current Plan
                </button>
              ) : user?.isTrialPremium && tier.role === "PREMIUM_USER" ? (
                <button
                className="membership-upgrade-button"
                onClick={() => {
                  setPendingTier(tier);
                  setShowMonthlyWarning(true);
                }}
              >
                Upgrade to Keep Premium
              </button>
              ) : user.role === "FREE_USER" ? (
                <button
                  className="membership-upgrade-button"
                  onClick={() => {
                    const selectedPriceId =
                      tier.prices[billingCycle]?.id || tier.priceId;

                    if (
                      billingCycle === "monthly" &&
                      tier.name === "Premium Tier"
                    ) {
                      setPendingTier(tier);
                      setShowMonthlyWarning(true);
                    } else {
                      handleUpgradeClick(selectedPriceId);
                    }
                  }}
                >
                  Upgrade to {tier.name}
                </button>
              ) // FINAL BUTTON RENDERING LOGIC
              : user?.isTrialPremium && tier.role === "PAID_USER" ? (
                <button
                  className="membership-upgrade-button"
                  onClick={() => {
                    const selectedPriceId = tier.prices[billingCycle]?.id;
                    handleUpgradeClick(selectedPriceId);
                  }}
                >
                  Start Basic Plan
                </button>
              ) : user?.isTrialPremium && tier.role !== "PREMIUM_USER" ? (
                <button
                  className="membership-upgrade-button disabled"
                  disabled
                  title="Plan management is disabled during your free trial"
                >
                  Manage Plan
                </button>
              ) : (
                <button
                  className={`membership-upgrade-button ${
                    isDowngradeOption(user.role, tier.role) ? "downgrade" : ""
                  }`}
                  onClick={handleOpenPortal}
                >
                  Manage Plan
                </button>
              )
              }
            </div>
          );
        })}

        {showMonthlyWarning && pendingTier && (
          <div className="switch-warning-modal">
            <div className="switch-warning-content">
              <h2>Choose Your Subscription</h2>
              <p>
                Would you like to subscribe with the <strong>Yearly</strong> or{" "}
                <strong>Monthly</strong> plan?
              </p>
              <div className="switch-warning-buttons">
                <button
                  className="switch-warning-yes"
                  onClick={() => {
                    handleUpgradeClick(pendingTier.prices.yearly.id);
                    setShowMonthlyWarning(false);
                    setPendingTier(null);
                  }}
                >
                  Yearly
                </button>
                <button
                  className="switch-warning-no"
                  onClick={() => {
                    handleUpgradeClick(pendingTier.prices.monthly.id);
                    setShowMonthlyWarning(false);
                    setPendingTier(null);
                  }}
                >
                  Monthly
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Membership;
